import React from 'react';
import { ChannelFormData } from '../types';

export interface NetworkingTabProps {
  formData: ChannelFormData;
  setFormData: React.Dispatch<React.SetStateAction<ChannelFormData>>;
  setIsModified: (modified: boolean) => void;
}

const NetworkingTab: React.FC<NetworkingTabProps> = ({
  formData,
  setFormData,
  setIsModified,
}) => {
  return (
    <>
      {/* Timeout Configuration */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="m-0 font-weight-bold text-primary">
              <i className="fas fa-clock"></i> Request Timeouts
            </h6>
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="timeout-enabled"
                checked={formData.timeout !== undefined && formData.timeout !== null}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormData({
                      ...formData,
                      timeout: {
                        request_secs: 30,
                        connect_secs: 10,
                        idle_secs: 60
                      }
                    });
                  } else {
                    setFormData({
                      ...formData,
                      timeout: undefined
                    });
                  }
                  setIsModified(true);
                }}
              />
              <label className="form-check-label" htmlFor="timeout-enabled" style={{ color: (formData.timeout !== undefined && formData.timeout !== null) ? '#000' : '#6c757d' }}>
                Enabled
              </label>
            </div>
          </div>
        </div>
        {formData.timeout !== undefined && formData.timeout !== null && (
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label fw-semibold">Request Timeout (seconds)</label>
                <input
                  type="number"
                  className="form-control"
                  min="1"
                  value={formData.timeout.request_secs}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      timeout: {
                        ...formData.timeout!,
                        request_secs: parseInt(e.target.value) || 30
                      }
                    });
                    setIsModified(true);
                  }}
                />
                <small className="form-text text-muted">Overall request duration limit</small>
              </div>
              <div className="col-md-4">
                <label className="form-label fw-semibold">Connect Timeout (seconds)</label>
                <input
                  type="number"
                  className="form-control"
                  min="1"
                  value={formData.timeout.connect_secs}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      timeout: {
                        ...formData.timeout!,
                        connect_secs: parseInt(e.target.value) || 10
                      }
                    });
                    setIsModified(true);
                  }}
                />
                <small className="form-text text-muted">Time to establish connection</small>
              </div>
              <div className="col-md-4">
                <label className="form-label fw-semibold">Idle Timeout (seconds)</label>
                <input
                  type="number"
                  className="form-control"
                  min="1"
                  value={formData.timeout.idle_secs}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      timeout: {
                        ...formData.timeout!,
                        idle_secs: parseInt(e.target.value) || 60
                      }
                    });
                    setIsModified(true);
                  }}
                />
                <small className="form-text text-muted">Time between data frames</small>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Retry Configuration */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="m-0 font-weight-bold text-primary">
              <i className="fas fa-redo"></i> Retry Logic
            </h6>
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="retry-enabled"
                checked={formData.retry !== undefined && formData.retry !== null}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormData({
                      ...formData,
                      retry: {
                        max_attempts: 3,
                        backoff_multiplier: 2.0,
                        initial_backoff_ms: 100,
                        max_backoff_ms: 5000,
                        retryable_status_codes: [408, 429, 500, 502, 503, 504]
                      }
                    });
                  } else {
                    setFormData({
                      ...formData,
                      retry: undefined
                    });
                  }
                  setIsModified(true);
                }}
              />
              <label className="form-check-label" htmlFor="retry-enabled" style={{ color: (formData.retry !== undefined && formData.retry !== null) ? '#000' : '#6c757d' }}>
                Enabled
              </label>
            </div>
          </div>
        </div>
        {formData.retry !== undefined && formData.retry !== null && (
          <div className="card-body">
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">Max Retry Attempts</label>
                <input
                  type="number"
                  className="form-control"
                  min="0"
                  max="10"
                  value={formData.retry.max_attempts}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      retry: {
                        ...formData.retry!,
                        max_attempts: parseInt(e.target.value) || 3
                      }
                    });
                    setIsModified(true);
                  }}
                />
                <small className="form-text text-muted">Number of times to retry failed requests</small>
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">Backoff Multiplier</label>
                <input
                  type="number"
                  className="form-control"
                  min="1"
                  step="0.1"
                  value={formData.retry.backoff_multiplier}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      retry: {
                        ...formData.retry!,
                        backoff_multiplier: parseFloat(e.target.value) || 2.0
                      }
                    });
                    setIsModified(true);
                  }}
                />
                <small className="form-text text-muted">Exponential backoff multiplier</small>
              </div>
            </div>
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">Initial Backoff (ms)</label>
                <input
                  type="number"
                  className="form-control"
                  min="1"
                  value={formData.retry.initial_backoff_ms}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      retry: {
                        ...formData.retry!,
                        initial_backoff_ms: parseInt(e.target.value) || 100
                      }
                    });
                    setIsModified(true);
                  }}
                />
                <small className="form-text text-muted">First retry delay</small>
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">Max Backoff (ms)</label>
                <input
                  type="number"
                  className="form-control"
                  min="1"
                  value={formData.retry.max_backoff_ms}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      retry: {
                        ...formData.retry!,
                        max_backoff_ms: parseInt(e.target.value) || 5000
                      }
                    });
                    setIsModified(true);
                  }}
                />
                <small className="form-text text-muted">Maximum retry delay</small>
              </div>
            </div>
            <div className="row g-3">
              <div className="col-12">
                <label className="form-label fw-semibold">Retryable Status Codes</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.retry.retryable_status_codes.join(',')}
                  onChange={(e) => {
                    const codes = e.target.value
                      .split(',')
                      .map(s => parseInt(s.trim()))
                      .filter(n => !isNaN(n));
                    setFormData({
                      ...formData,
                      retry: {
                        ...formData.retry!,
                        retryable_status_codes: codes.length > 0 ? codes : [408, 429, 500, 502, 503, 504]
                      }
                    });
                    setIsModified(true);
                  }}
                  placeholder="408,429,500,502,503,504"
                />
                <small className="form-text text-muted">Comma-separated HTTP status codes that trigger retries</small>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Circuit Breaker Configuration */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="m-0 font-weight-bold text-primary">
              <i className="fas fa-exclamation-triangle"></i> Circuit Breaker
            </h6>
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="cb-enabled"
                checked={formData.circuit_breaker !== undefined && formData.circuit_breaker !== null}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormData({
                      ...formData,
                      circuit_breaker: {
                        failure_threshold: 5,
                        success_threshold: 2,
                        timeout_secs: 60,
                        window_secs: 60
                      }
                    });
                  } else {
                    setFormData({
                      ...formData,
                      circuit_breaker: undefined
                    });
                  }
                  setIsModified(true);
                }}
              />
              <label className="form-check-label" htmlFor="cb-enabled" style={{ color: (formData.circuit_breaker !== undefined && formData.circuit_breaker !== null) ? '#000' : '#6c757d' }}>
                Enabled
              </label>
            </div>
          </div>
        </div>
        {formData.circuit_breaker !== undefined && formData.circuit_breaker !== null && (
          <div className="card-body">
            <div className="row g-3 mb-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">Failure Threshold</label>
                <input
                  type="number"
                  className="form-control"
                  min="1"
                  value={formData.circuit_breaker.failure_threshold}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      circuit_breaker: {
                        ...formData.circuit_breaker!,
                        failure_threshold: parseInt(e.target.value) || 5
                      }
                    });
                    setIsModified(true);
                  }}
                />
                <small className="form-text text-muted">Consecutive failures before opening circuit</small>
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">Success Threshold</label>
                <input
                  type="number"
                  className="form-control"
                  min="1"
                  value={formData.circuit_breaker.success_threshold}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      circuit_breaker: {
                        ...formData.circuit_breaker!,
                        success_threshold: parseInt(e.target.value) || 2
                      }
                    });
                    setIsModified(true);
                  }}
                />
                <small className="form-text text-muted">Consecutive successes to close from half-open</small>
              </div>
            </div>
            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label fw-semibold">Timeout (seconds)</label>
                <input
                  type="number"
                  className="form-control"
                  min="1"
                  value={formData.circuit_breaker.timeout_secs}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      circuit_breaker: {
                        ...formData.circuit_breaker!,
                        timeout_secs: parseInt(e.target.value) || 60
                      }
                    });
                    setIsModified(true);
                  }}
                />
                <small className="form-text text-muted">Time before attempting half-open state</small>
              </div>
              <div className="col-md-6">
                <label className="form-label fw-semibold">Window (seconds)</label>
                <input
                  type="number"
                  className="form-control"
                  min="1"
                  value={formData.circuit_breaker.window_secs}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      circuit_breaker: {
                        ...formData.circuit_breaker!,
                        window_secs: parseInt(e.target.value) || 60
                      }
                    });
                    setIsModified(true);
                  }}
                />
                <small className="form-text text-muted">Time window for counting failures</small>
              </div>
            </div>
            <div className="alert alert-info mt-3 mb-0">
              <i className="fas fa-info-circle me-2"></i>
              <strong>Circuit States:</strong> Closed (normal) → Open (failing fast) → Half-Open (testing recovery) → Closed
            </div>
          </div>
        )}
      </div>
      
      {/* Rate Limiting Configuration */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="m-0 font-weight-bold text-primary">
              <i className="fas fa-tachometer-alt"></i> Rate Limiting
            </h6>
            <div className="form-check mb-0">
              <input
                type="checkbox"
                className="form-check-input"
                id="rate-limit-enabled"
                checked={formData.rate_limit !== undefined && formData.rate_limit !== null}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormData({
                      ...formData,
                      rate_limit: {
                        requests: 100,
                        window_secs: 60,
                        burst: 10
                      }
                    });
                  } else {
                    setFormData({
                      ...formData,
                      rate_limit: undefined
                    });
                  }
                  setIsModified(true);
                }}
                style={{ cursor: 'pointer' }}
              />
              <label className="form-check-label" htmlFor="rate-limit-enabled" style={{ color: (formData.rate_limit !== undefined && formData.rate_limit !== null) ? '#000' : '#6c757d' }}>Enabled</label>
            </div>
          </div>
        </div>
        {formData.rate_limit !== undefined && formData.rate_limit !== null && (
        <div className="card-body">
          <p className="text-muted mb-3">
            Limit the number of requests to prevent abuse. Applies to {formData.protocol === 'mcp' ? 'MCP' : formData.protocol === 'mpx' ? 'MPX' : 'A2A'} requests.
          </p>
          <>
            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <label className="form-label fw-semibold">Requests per Window</label>
                <input
                  type="number"
                  className="form-control"
                  min="1"
                  value={formData.rate_limit.requests}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      rate_limit: {
                        ...formData.rate_limit!,
                        requests: parseInt(e.target.value) || 100
                      }
                    });
                    setIsModified(true);
                  }}
                />
                <small className="form-text text-muted">Maximum requests allowed in the time window</small>
              </div>
              <div className="col-md-4">
                <label className="form-label fw-semibold">Window Size (seconds)</label>
                <input
                  type="number"
                  className="form-control"
                  min="1"
                  value={formData.rate_limit.window_secs}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      rate_limit: {
                        ...formData.rate_limit!,
                        window_secs: parseInt(e.target.value) || 60
                      }
                    });
                    setIsModified(true);
                  }}
                />
                <small className="form-text text-muted">Time window for rate limiting</small>
              </div>
              <div className="col-md-4">
                <label className="form-label fw-semibold">Burst Size</label>
                <input
                  type="number"
                  className="form-control"
                  min="1"
                  value={formData.rate_limit.burst}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      rate_limit: {
                        ...formData.rate_limit!,
                        burst: parseInt(e.target.value) || 10
                      }
                    });
                    setIsModified(true);
                  }}
                />
                <small className="form-text text-muted">Maximum burst requests allowed</small>
              </div>
            </div>
            
            <div className="alert alert-info mb-0">
              <div className="d-flex">
                <i className="fas fa-info-circle me-2 mt-1"></i>
                <div>
                  <strong className="d-block mb-2">Token Bucket Algorithm</strong>
                  <ul className="mb-0 ps-3">
                    <li><strong>Requests per Window:</strong> Steady-state rate (e.g., 100 requests per 60 seconds ≈ 1.67 req/sec)</li>
                    <li><strong>Burst Size:</strong> Allow temporary spikes above the steady-state rate</li>
                    <li><strong>Error Response:</strong> {(formData.protocol === 'mcp' || formData.protocol === 'mpx') ? 'JSON-RPC error with code -32000' : 'HTTP 429 Too Many Requests'} with retry-after information</li>
                  </ul>
                </div>
              </div>
            </div>
          </>
        </div>
        )}
      </div>
      
      {/* Traffic Mirroring Configuration */}
      <div className="card shadow-sm mb-4">
        <div className="card-header bg-light">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="m-0 font-weight-bold text-primary">
              <i className="fas fa-clone"></i> Traffic Mirroring
            </h6>
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="mirror-enabled"
                checked={formData.mirror !== undefined && formData.mirror !== null}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFormData({
                      ...formData,
                      mirror: {
                        endpoint: '',
                        percentage: 100,
                        timeout_secs: 5,
                        wait_for_response: false
                      }
                    });
                  } else {
                    setFormData({
                      ...formData,
                      mirror: undefined
                    });
                  }
                  setIsModified(true);
                }}
              />
              <label className="form-check-label" htmlFor="mirror-enabled" style={{ color: (formData.mirror !== undefined && formData.mirror !== null) ? '#000' : '#6c757d' }}>
                Enabled
              </label>
            </div>
          </div>
        </div>
        {formData.mirror !== undefined && formData.mirror !== null && (
          <div className="card-body">
            <div className="row g-3 mb-3">
              <div className="col-12">
                <label className="form-label fw-semibold">Mirror Endpoint URL</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.mirror.endpoint}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      mirror: {
                        ...formData.mirror!,
                        endpoint: e.target.value
                      }
                    });
                    setIsModified(true);
                  }}
                  placeholder="https://test-backend.example.com"
                />
                <small className="form-text text-muted">Target endpoint for mirrored requests</small>
              </div>
            </div>
            <div className="row g-3 mb-3">
              <div className="col-md-4">
                <label className="form-label fw-semibold">Traffic Percentage</label>
                <input
                  type="number"
                  className="form-control"
                  min="0"
                  max="100"
                  value={formData.mirror.percentage}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      mirror: {
                        ...formData.mirror!,
                        percentage: parseInt(e.target.value) || 100
                      }
                    });
                    setIsModified(true);
                  }}
                />
                <small className="form-text text-muted">Percentage of requests to mirror (0-100)</small>
              </div>
              <div className="col-md-4">
                <label className="form-label fw-semibold">Timeout (seconds)</label>
                <input
                  type="number"
                  className="form-control"
                  min="1"
                  value={formData.mirror.timeout_secs}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      mirror: {
                        ...formData.mirror!,
                        timeout_secs: parseInt(e.target.value) || 5
                      }
                    });
                    setIsModified(true);
                  }}
                />
                <small className="form-text text-muted">Mirror request timeout</small>
              </div>
              <div className="col-md-4">
                <label className="form-label fw-semibold">Synchronicity</label>
                <div className="form-check mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="mirror-wait"
                    checked={!formData.mirror.wait_for_response}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        mirror: {
                          ...formData.mirror!,
                          wait_for_response: !e.target.checked
                        }
                      });
                      setIsModified(true);
                    }}
                  />
                  <label className="form-check-label" htmlFor="mirror-wait">
                    Fire-and-forget (async)
                  </label>
                </div>
              </div>
            </div>
            <div className="alert alert-warning mb-0">
              <i className="fas fa-exclamation-triangle me-2"></i>
              <strong>Note:</strong> Mirror requests include an <code>X-Mirrored-Request: true</code> header
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default NetworkingTab;
